import { dispatch, make } from 'vuex-pathify';
import { ForeclosureLoansService } from '@/modules/delinquency/services/foreclosure-loans.service';
import { OnBoarderAccountsService } from '@/modules/onboarder/services/onboarder-accounts.service';

import Logger from '@/lib/logger.js';
import { merge } from 'lodash';

const service = new ForeclosureLoansService();
const onBoarderAccountService = new OnBoarderAccountsService();

export const state = {
  foreclosureListing: [],
  foreclosureListingTotal: 0,
  currentForeclosureLoan: null,
  currentAccountData: null,
  currentCaseById: null,
  personsOnCurrentCase: [],
  calculatedForeclosedCollateral: []
};

export const mutations = {
  ...make.mutations(state)
};

export const getters = {
  ...make.getters(state)
};

export const actions = {
  async calculateCashRealizedValue({ commit }, collateralIds) {
    try {
      const { data } = await service.calculateCashRealizedValue(collateralIds);
      commit('SET_CALCULATED_FORECLOSED_COLLATERAL', data.data);
      return data;
    } catch (error) {
      Logger.error('Finding Foreclosed Collateral For Loans', error);
      throw error;
    }
  },

  /**
   * Sends a request to update assets
   */
  async updateAssets() {
    try {
      const { data } = await service.updateAssets();
      return data;
    } catch (error) {
      Logger.error('Updating Assets');
      throw error;
    }
  },
  /**
   * Get foreclosure listing
   */
  async getForeclosureListing({ commit }, params) {
    const defaults = {
      page: 1,
      limit: 20,
      orderBy: 'asc',
      sortBy: 'borrowerName',
      /*assignmentStatus: true*/
    };
    // Merge defaults with user options
    const mergedParams = merge({}, defaults, params);
    try {
      commit('SET_FORECLOSURE_LISTING', []);
      const { data } = await service.getForeclosureListing(mergedParams);
      commit('SET_FORECLOSURE_LISTING', data.records);
      commit('SET_FORECLOSURE_LISTING_TOTAL', data.total);
      return data;
    } catch (error) {
      Logger.error('Getting Foreclosure Listing');
      throw error;
    }
  },
  async assignForeclosureCase(_, params) {
    try {
      const { data } = await service.assignForeclosureCase(params);
      return data;
    } catch (error) {
      Logger.error('Assigning Foreclosure Loans');
      throw error;
    }
  },
  async deAssignForeclosureCase(_, params) {
    try {
      const { data } = await service.deAssignForeclosureCase(params);
      return data;
    } catch (error) {
      Logger.error('De-assigning Foreclosure Loans');
      throw error;
    }
  },
  async getAccountByLoanIdAccountNumber({ commit }, id) {
    try {
      const { data } = await onBoarderAccountService.getAccountByLoanIdAccountNumber(id);
      commit('SET_CURRENT_ACCOUNT_DATA', data);
      return data;
    } catch (error) {
      Logger.error('Getting Account By Loan Id Account Number');
      throw error;
    }
  },
  async updateForeclosureCase(_, { caseId, data }) {
    try {
      const { data: res } = await service.updateForeclosureCase(caseId, data);
      return res;
    } catch (error) {
      Logger.error('Updating Foreclosure Status');
      throw error;
    }
  },
  async createForeClosureCase(_, data) {
    try {
      const { data: res } = await service.createForeClosureCase(data);
      return res;
    } catch (error) {
      Logger.error('Creating Foreclosure Case');
      throw error;
    }
  },

  async findForeClosureCasesById({ commit }, id) {
    try {
      let { data } = await service.findForeClosureCasesByLoanIdOrCaseId(id);
      data = data[0];
      if (data) {
        data['activeForeclosureAssignment'] = data?.foreclosureAssignment[0] || {};
        commit('SET_CURRENT_CASE_BY_ID', data);
      }
      return data;
    } catch (error) {
      Logger.error('Finding Foreclosure Cases By Loan Id Or Case Id');
      throw error;
    }
  },
  async getPledgedAssetsByBorrower(_, info) {
    try {
      const { data } = await service.getPledgedAssetsByBorrower(info.borrowerId, info.loanId);
      return data;
    } catch (error) {
      Logger.error('Getting Pledged Assets By Borrower');
      throw error;
    }
  },
  async removeCollateralFromForeclosureList(_, { collateralId, data }) {
    try {
      const { data: res } = await service.removeCollateralFromForeclosureList(collateralId, data);
      return res;
    } catch (error) {
      Logger.error('Removing Collateral From Foreclosure List');
      throw error;
    }
  },
  async addCollateralToForeclosureList(_, { collateralId, data }) {
    try {
      const { data: res } = await service.addCollateralToForeclosureList(collateralId, data);
      return res;
    } catch (error) {
      Logger.error('Adding Collateral To Foreclosure List');
      throw error;
    }
  },
  async updateForeclosureStatusCollateral(_, { collateralId, data }) {
    try {
      const { data: res } = await service.updateForeclosureStatusCollateral(collateralId, data);
      return res;
    } catch (error) {
      Logger.error('Updating Foreclosure Status Collateral');
      throw error;
    }
  },
  async updateDelinquencyCollateralAssignment({ dispatch }, { id, data }) {
    try {
      const { data: res } = await dispatch('delinquencyCollateralAssignmentModule/updateDelinquencyCollateralAssignment', { id, data }, { root: true });
      return res;
    } catch (error) {
      Logger.error('Updating Delinquency Collateral Assignment');
      throw error;
    }
  },
  async deAssignDelinquencyCollateralAssignments({ dispatch }, ids) {
    try {
      const data = await dispatch('delinquencyCollateralAssignmentModule/unAssignDelinquencyCollateralAssignments', ids, { root: true });
      return data;
    } catch (error) {
      Logger.error('De-assigning Delinquency Collateral Assignment');
      throw error;
    }
  },
  async findDelinquencyCollateralAssignmentByCollateralId({ dispatch }, collateralId) {
    try {
      const data = await dispatch('delinquencyCollateralAssignmentModule/findDelinquencyCollateralAssignment', collateralId, { root: true });
      return data;
    } catch (error) {
      Logger.error('Finding Delinquency Collateral Assignment By Collateral Id');
      throw error;
    }
  },
  async updateCollateralSaleInformation(_, { collateralId, data }) {
    try {
      const { data: res } = await service.updateCollateralSaleInformation(collateralId, data);
      return res;
    } catch (error) {
      Logger.error('Updating Collateral Sale Information');
      throw error;
    }
  },
  
};
